import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, formatNum, presetRange, todayStr } from "@/lib/format";
import { SectionTitle, MetricCard } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Briefcase, Plus, Trash2, AlertTriangle, TrendingUp, Lightbulb, Target as TargetIcon,
  ClipboardCheck, BarChart4,
} from "lucide-react";

const GAP_TYPES = ["Low calls", "Low connect", "Low conversion", "Follow-up gap", "Objection handling", "Discipline", "Other"];
const SEVERITY = ["Low", "Medium", "High"];
const STATUS = ["Pending", "In Progress", "Completed"];

function useCollection(kind) {
  const [rows, setRows] = useState([]);
  const reload = () => api.managerList(kind).then(setRows);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kind]);
  const create = async (data) => { await api.managerCreate(kind, data); reload(); toast.success("Added"); };
  const update = async (id, data) => { await api.managerUpdate(kind, id, data); reload(); };
  const remove = async (id) => { await api.managerDelete(kind, id); reload(); toast.success("Deleted"); };
  return { rows, create, update, remove };
}

function EmpSelect({ value, onChange, employees, testid }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="rounded-lg" data-testid={testid}><SelectValue placeholder="Employee / Team" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Whole Team</SelectItem>
        {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function GapsTab({ employees }) {
  const { rows, create, remove } = useCollection("gaps");
  const [f, setF] = useState({ employee_id: "all", gap_type: "", severity: "Medium", note: "", date: todayStr() });
  const [evidence, setEvidence] = useState(null);

  const loadEvidence = async () => {
    const r = presetRange("this_month");
    setEvidence(await api.kpiEvidence(f.employee_id, r.start, r.end));
  };
  useEffect(() => { loadEvidence(); /* eslint-disable-next-line */ }, [f.employee_id]);

  const add = () => {
    if (!f.gap_type) { toast.error("Pick a gap type"); return; }
    const emp = employees.find((e) => e.id === f.employee_id);
    create({ ...f, employee_name: emp?.name || "Team", evidence });
    setF({ employee_id: "all", gap_type: "", severity: "Medium", note: "", date: todayStr() });
  };

  return (
    <div className="space-y-4">
      <div className="surface p-5">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-2">
          <EmpSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} employees={employees} testid="gap-employee" />
          <Select value={f.gap_type || undefined} onValueChange={(v) => setF({ ...f, gap_type: v })}>
            <SelectTrigger className="rounded-lg" data-testid="gap-type"><SelectValue placeholder="Gap found" /></SelectTrigger>
            <SelectContent>{GAP_TYPES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}>
            <SelectTrigger className="rounded-lg" data-testid="gap-severity"><SelectValue /></SelectTrigger>
            <SelectContent>{SEVERITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" className="rounded-lg" data-testid="gap-note" />
          <Button onClick={add} className="rounded-lg bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]" data-testid="add-gap-btn"><Plus className="h-4 w-4 mr-1" /> Log</Button>
        </div>
        {evidence && (
          <div className="text-xs text-slate-400 surface-2 px-3 py-2 font-mono">
            Auto-evidence (this month): {formatNum(evidence.dialed)} dialed · {evidence.connect_rate}% connect · {formatNum(evidence.total_visits)} visits · {evidence.treat_conv}% conv · {formatINR(evidence.revenue)}
          </div>
        )}
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No gaps logged yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="surface p-4 flex items-start gap-3" data-testid={`gap-item-${r.id}`}>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${r.severity === "High" ? "bg-rose-500/15 text-rose-300" : r.severity === "Medium" ? "bg-amber-500/15 text-amber-300" : "bg-white/10 text-slate-400"}`}><AlertTriangle className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="font-semibold text-slate-100 text-sm">{r.gap_type} — {r.employee_name} <span className={`text-[11px] ml-1 px-1.5 py-0.5 rounded ${r.severity === "High" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}>{r.severity}</span></div>
              {r.note && <div className="text-sm text-slate-400 mt-0.5">{r.note}</div>}
              {r.evidence && <div className="text-[11px] text-slate-500 font-mono mt-1">{formatNum(r.evidence.dialed)} dialed · {r.evidence.connect_rate}% connect · {formatINR(r.evidence.revenue)}</div>}
            </div>
            <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`del-gap-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImprovementsTab({ employees }) {
  const { rows, create, update, remove } = useCollection("improvements");
  const [f, setF] = useState({ employee_id: "all", action: "", owner: "", start_date: todayStr(), due_date: "", status: "Pending" });

  const add = () => {
    if (!f.action) { toast.error("Add an action"); return; }
    const emp = employees.find((e) => e.id === f.employee_id);
    create({ ...f, employee_name: emp?.name || "Team", before: null, after: null });
    setF({ employee_id: "all", action: "", owner: "", start_date: todayStr(), due_date: "", status: "Pending" });
  };

  const captureBeforeAfter = async (r) => {
    const before = await api.kpiEvidence(r.employee_id, r.start_date, r.start_date);
    const after = await api.kpiEvidence(r.employee_id, r.due_date || todayStr(), todayStr());
    update(r.id, { before, after }); toast.success("Before/after captured");
  };

  return (
    <div className="space-y-4">
      <div className="surface p-5 grid grid-cols-1 sm:grid-cols-6 gap-2">
        <EmpSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} employees={employees} testid="imp-employee" />
        <Input value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Action taken" className="rounded-lg sm:col-span-2" data-testid="imp-action" />
        <Input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} placeholder="Owner" className="rounded-lg" data-testid="imp-owner" />
        <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="rounded-lg" data-testid="imp-due" />
        <Button onClick={add} className="rounded-lg bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]" data-testid="add-imp-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No improvement actions yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="surface p-4" data-testid={`imp-item-${r.id}`}>
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-sky-500/15 text-sky-300 flex items-center justify-center"><TrendingUp className="h-4 w-4" /></div>
              <div className="flex-1">
                <div className="font-semibold text-slate-100 text-sm">{r.action} — {r.employee_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">Owner: {r.owner || "—"} · Due: {r.due_date || "—"}</div>
                {r.before && r.after && <div className="text-[11px] text-slate-400 font-mono mt-1">Before→After revenue {formatINR(r.before.revenue)} → {formatINR(r.after.revenue)} · conv {r.before.treat_conv}% → {r.after.treat_conv}%</div>}
              </div>
              <div className="flex items-center gap-2">
                <Select value={r.status} onValueChange={(v) => update(r.id, { status: v })}>
                  <SelectTrigger className="h-8 w-32 rounded-lg text-xs" data-testid={`imp-status-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => captureBeforeAfter(r)} data-testid={`imp-capture-${r.id}`}>KPI</Button>
                <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`mw-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StrategiesTab({ employees }) {
  const { rows, create, update, remove } = useCollection("strategies");
  const [f, setF] = useState({ strategy: "", applies_to: "all", date: todayStr(), expected_impact: "" });
  const add = () => {
    if (!f.strategy) { toast.error("Describe the strategy"); return; }
    const emp = employees.find((e) => e.id === f.applies_to);
    create({ ...f, applies_name: emp?.name || "Team", impact_positive: false, result: "" });
    setF({ strategy: "", applies_to: "all", date: todayStr(), expected_impact: "" });
  };
  return (
    <div className="space-y-4">
      <div className="surface p-5 grid grid-cols-1 sm:grid-cols-6 gap-2">
        <Input value={f.strategy} onChange={(e) => setF({ ...f, strategy: e.target.value })} placeholder="Strategy (coaching, script change…)" className="rounded-lg sm:col-span-2" data-testid="strat-text" />
        <EmpSelect value={f.applies_to} onChange={(v) => setF({ ...f, applies_to: v })} employees={employees} testid="strat-applies" />
        <Input value={f.expected_impact} onChange={(e) => setF({ ...f, expected_impact: e.target.value })} placeholder="Expected impact" className="rounded-lg sm:col-span-2" data-testid="strat-impact" />
        <Button onClick={add} className="rounded-lg bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]" data-testid="add-strat-btn"><Plus className="h-4 w-4 mr-1" /> Log</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No strategies logged yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="surface p-4 flex items-start gap-3" data-testid={`strat-item-${r.id}`}>
            <div className="h-9 w-9 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center"><Lightbulb className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="font-semibold text-slate-100 text-sm">{r.strategy} — {r.applies_name}</div>
              <div className="text-xs text-slate-500 mt-0.5">Expected: {r.expected_impact || "—"} · {r.date}</div>
              <div className="flex items-center gap-2 mt-2">
                <Input value={r.result} onChange={(e) => update(r.id, { result: e.target.value })} placeholder="Result after 7/15/30 days" className="h-8 rounded-lg text-xs" data-testid={`strat-result-${r.id}`} />
                <label className="flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap cursor-pointer">
                  <input type="checkbox" checked={r.impact_positive || false} onChange={(e) => update(r.id, { impact_positive: e.target.checked })} data-testid={`strat-positive-${r.id}`} /> Positive
                </label>
              </div>
            </div>
            <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`mw-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManagerTargetsTab({ employees }) {
  const { rows, create, remove } = useCollection("targets");
  const [f, setF] = useState({ employee_id: "all", period: "weekly", metric: "calls", target: 0, note: "" });
  const METRICS = ["calls", "visits", "leads", "revenue", "treatments"];
  const add = () => {
    if (!f.target) { toast.error("Set a target value"); return; }
    const emp = employees.find((e) => e.id === f.employee_id);
    create({ ...f, employee_name: emp?.name || "Team" });
    setF({ employee_id: "all", period: "weekly", metric: "calls", target: 0, note: "" });
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Set weekly/daily sub-targets per employee within the Owner's monthly goals.</p>
      <div className="surface p-5 grid grid-cols-1 sm:grid-cols-6 gap-2">
        <EmpSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} employees={employees} testid="mtarget-employee" />
        <Select value={f.period} onValueChange={(v) => setF({ ...f, period: v })}>
          <SelectTrigger className="rounded-lg" data-testid="mtarget-period"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
        </Select>
        <Select value={f.metric} onValueChange={(v) => setF({ ...f, metric: v })}>
          <SelectTrigger className="rounded-lg" data-testid="mtarget-metric"><SelectValue /></SelectTrigger>
          <SelectContent>{METRICS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" value={f.target || ""} onChange={(e) => setF({ ...f, target: parseInt(e.target.value || "0", 10) })} placeholder="Target" className="rounded-lg" data-testid="mtarget-value" />
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" className="rounded-lg" data-testid="mtarget-note" />
        <Button onClick={add} className="rounded-lg bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]" data-testid="add-mtarget-btn"><Plus className="h-4 w-4 mr-1" /> Set</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No sub-targets set yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="surface p-4 flex items-center gap-3" data-testid={`mtarget-item-${r.id}`}>
            <div className="h-9 w-9 rounded-lg bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center"><TargetIcon className="h-4 w-4" /></div>
            <div className="flex-1">
              <div className="font-semibold text-slate-100 text-sm capitalize">{r.period} {r.metric} target — {r.employee_name}</div>
              <div className="text-xs text-slate-500 mt-0.5 font-mono">{r.metric === "revenue" ? formatINR(r.target) : formatNum(r.target)}{r.note ? ` · ${r.note}` : ""}</div>
            </div>
            <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`mw-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsTab({ employees }) {
  const { rows, create, remove } = useCollection("reviews");
  const [f, setF] = useState({ employee_id: "all", date: todayStr(), note: "" });
  const add = () => {
    if (!f.note) { toast.error("Add a review note"); return; }
    const emp = employees.find((e) => e.id === f.employee_id);
    create({ ...f, employee_name: emp?.name || "Team" });
    setF({ employee_id: "all", date: todayStr(), note: "" });
  };
  return (
    <div className="space-y-4">
      <div className="surface p-5 grid grid-cols-1 sm:grid-cols-6 gap-2">
        <EmpSelect value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} employees={employees} testid="rev-employee" />
        <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Weekly review / coaching note" className="rounded-lg sm:col-span-4" data-testid="rev-note" />
        <Button onClick={add} className="rounded-lg bg-[#C9A24B] hover:bg-[#b8923f] text-[#0b1120]" data-testid="add-rev-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No review notes yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="surface p-4 flex items-start gap-3" data-testid={`rev-item-${r.id}`}>
            <div className="h-9 w-9 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center"><ClipboardCheck className="h-4 w-4" /></div>
            <div className="flex-1"><div className="font-semibold text-slate-100 text-sm">{r.employee_name} · {r.date}</div><div className="text-sm text-slate-400 mt-0.5">{r.note}</div></div>
            <button onClick={() => remove(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`mw-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ManagerWorkspace() {
  const { activeEmployees } = useApp();
  const employees = activeEmployees.filter((e) => e.role === "telecaller");
  const [score, setScore] = useState(null);

  useEffect(() => {
    const r = presetRange("this_month");
    api.managerScorecard(r.start, r.end).then(setScore);
  }, []);

  return (
    <div className="animate-fade-up">
      <SectionTitle icon={Briefcase} title="Manager Workspace" desc="Gap analysis, improvements, strategies & accountability" />

      {score && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard label="Team Achievement" value={`${score.team_achievement}`} sub="avg score /100" icon={BarChart4} tone="gold" />
          <MetricCard label="Gaps Closed" value={`${score.gaps_closed}/${score.gaps_identified}`} sub={`${score.improvement_rate}% improvement`} icon={TrendingUp} tone="sky" />
          <MetricCard label="Positive Strategies" value={`${score.positive_strategies}/${score.total_strategies}`} sub={`${score.review_sessions} reviews`} icon={Lightbulb} tone="indigo" />
          <MetricCard label="Entry Completion" value={`${score.entry_completion}%`} sub="on-time filling" icon={ClipboardCheck} tone="emerald" />
        </div>
      )}

      <Tabs defaultValue="gaps">
        <TabsList className="bg-white/5 rounded-xl p-1 mb-5 flex-wrap h-auto">
          <TabsTrigger value="gaps" data-testid="mw-tab-gaps" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white">Gap Analysis</TabsTrigger>
          <TabsTrigger value="improvements" data-testid="mw-tab-improvements" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white">Improvement Tracker</TabsTrigger>
          <TabsTrigger value="strategies" data-testid="mw-tab-strategies" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white">Strategy Log</TabsTrigger>
          <TabsTrigger value="mtargets" data-testid="mw-tab-mtargets" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white">Targets</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="mw-tab-reviews" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white">Reviews</TabsTrigger>
        </TabsList>
        <TabsContent value="gaps"><GapsTab employees={employees} /></TabsContent>
        <TabsContent value="improvements"><ImprovementsTab employees={employees} /></TabsContent>
        <TabsContent value="strategies"><StrategiesTab employees={employees} /></TabsContent>
        <TabsContent value="mtargets"><ManagerTargetsTab employees={employees} /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab employees={employees} /></TabsContent>
      </Tabs>
    </div>
  );
}
